import type { RegionSelectValueType, OptionsItemType } from '@home/components/NewRegionSelect/type';
import type { ISelectedChartTagData } from '../components/SelectedChartTag';
import { resolveGameSelectOption } from './gameSelectOption';

/**
 * 从外部 selectedCharts 反推出指定 gameCode 对应的 RegionSelectValueType
 * - dashboard 类型 -> regions + 展开其全部 children 到 markets
 * - chart 类型 -> markets
 */
export const deriveSelectValue = (
  selectedCharts: ISelectedChartTagData[],
  gameCode: string,
  dashboardOptions: OptionsItemType[],
): RegionSelectValueType => {
  const regions: string[] = [];
  const markets: string[] = [];
  const childrenValueMap = new Map<string, string[]>(
    dashboardOptions.map((option) => [option.value, (option.children || []).map((child) => child.value)]),
  );

  selectedCharts.forEach((chart) => {
    if (chart.gameCode !== gameCode) return;

    if (chart.type === 'dashboard') {
      regions.push(chart.chartId);
      markets.push(...(childrenValueMap.get(chart.chartId) || []));
      return;
    }

    // children.value 协议 = `${option.value}:${chart_id}`，dashboardValue = option.value
    markets.push(`${chart.dashboardValue}:${chart.chartId}`);
  });

  return { type: '', regions, markets };
};

/**
 * 将 localSelectValue + dashboardOptions 转换为结构化的 ISelectedChartTagData[]
 * （与 deriveSelectValue 互为逆操作）
 */
export const buildChartTagData = (
  localSelectValue: RegionSelectValueType,
  dashboardOptions: OptionsItemType[],
  gameCode: string,
  mainGames: any[],
  locale: string,
  fallbackGameIcon: string,
  fallbackDashboardIcon: string,
): ISelectedChartTagData[] => {
  const selectedRegions = localSelectValue?.regions || [];
  const selectedMarkets = localSelectValue?.markets || [];
  const matchedGame = resolveGameSelectOption(gameCode, mainGames, locale);
  const gameIcon = matchedGame?.icon || fallbackGameIcon;
  const gameName = matchedGame?.text || gameCode;

  const result: ISelectedChartTagData[] = [];

  for (const dashboardOption of dashboardOptions) {
    const dashboardNameEN = String(dashboardOption.text || '');
    const dashboardNameCN = String(dashboardOption.textCN || dashboardOption.text || '');
    // option.value 是拼接 id（防撞），realDashboardId 才是后端协议要的真实 id；旧数据 fallback 到 value
    const realDashboardId = (dashboardOption as any).realDashboardId || dashboardOption.value;
    const logDashboardTitleCN = String(
      (dashboardOption as { logDashboardTitleCN?: string; }).logDashboardTitleCN || '',
    );

    if (selectedRegions.includes(dashboardOption.value)) {
      const dashboardChartIds = (dashboardOption.children || [])
        .map((chartOption) => chartOption.value.split(':').slice(1).join(':'))
        .filter(Boolean);
      const dashboardChartNames = (dashboardOption.children || [])
        .map((chartOption) => String(chartOption.textCN || chartOption.text || ''))
        .filter(Boolean);

      result.push({
        chartId: dashboardOption.value,
        uniqueKey: `${gameCode}:${dashboardOption.value}:${dashboardOption.value}`,
        nameEN: dashboardNameEN,
        nameCN: dashboardNameCN,
        dashboardNameCN: logDashboardTitleCN || dashboardNameCN,
        gameIcon,
        chartTypeIcon: fallbackDashboardIcon,
        gameName,
        type: 'dashboard',
        gameCode,
        dashboardValue: dashboardOption.value,
        realDashboardId,
        dashboardChartIds,
        dashboardChartNames,
      });

      continue;
    }

    for (const chartOption of (dashboardOption.children || []).filter((child) => selectedMarkets.includes(child.value))) {
      const rawChartId = chartOption.value.split(':').slice(1).join(':');
      result.push({
        chartId: rawChartId,
        uniqueKey: `${gameCode}:${dashboardOption.value}:${rawChartId}`,
        nameEN: String(chartOption.text || ''),
        nameCN: String(chartOption.textCN || chartOption.text || ''),
        dashboardNameCN: logDashboardTitleCN || dashboardNameCN,
        gameIcon,
        chartTypeIcon: (typeof chartOption.icon === 'string' && chartOption.icon) ? chartOption.icon : fallbackDashboardIcon,
        chartRealType: String((chartOption as any).chartType || ''),
        gameName,
        type: 'chart',

        gameCode,
        dashboardValue: dashboardOption.value,
        realDashboardId,
      });
    }
  }

  return result;
};

interface IBuildOpenPanelValueParams {
  selectedCharts: ISelectedChartTagData[];
  gameCode: string;
  dashboardOptions: OptionsItemType[];
  /** 外部入口指定的目标 dashboard u_id；同时也是面板左侧默认高亮项 */
  dashboardValue?: string;
  /**
   * 是否自动追加勾选 dashboardValue 对应的整张报表（含全部子图表）。
   * 仅在「外部入口自动打开」场景为 true；用户手动点击 tag/icon 打开时一律 false，
   * 只做高亮、不改动选中态
   */
  autoSelect?: boolean;
}

interface IResolveDefaultSelectedRegionParams {
  value: RegionSelectValueType;
  dashboardOptions: OptionsItemType[];
  dashboardValue?: string;
}


/**
 * 生成面板打开时的初始 value：
 * - 基础态：从 selectedCharts 反推已选中项
 * - autoSelect=true 且携带 dashboardValue：在已选基础上额外勾选该报表（regions + 全部子图表 markets）
 */

export const buildOpenPanelValue = ({
  selectedCharts,
  gameCode,
  dashboardOptions,
  dashboardValue,
  autoSelect = false,
}: IBuildOpenPanelValueParams): RegionSelectValueType => {
  const baseValue = deriveSelectValue(selectedCharts, gameCode, dashboardOptions);

  if (!autoSelect || !dashboardValue) {
    return baseValue;
  }

  const targetDashboard = dashboardOptions.find((opt) => opt.value === dashboardValue);
  if (!targetDashboard) {
    return baseValue;
  }

  const childrenValues = (targetDashboard.children || []).map((child) => child.value);

  return {
    type: '',
    regions: Array.from(new Set([...(baseValue.regions || []), dashboardValue])),
    markets: Array.from(new Set([...(baseValue.markets || []), ...childrenValues])),
  };
};

/**
 * 解析打开面板时左侧默认展开的 dashboard：
 * - 优先使用外部指定的 dashboardValue（如点击 tag 回点）
 * - 其次使用 value.regions 的首项
 * - 最后从 markets 首项反推 parent dashboard
 */
export const resolveDefaultSelectedRegion = ({
  value,
  dashboardOptions,
  dashboardValue,
}: IResolveDefaultSelectedRegionParams): string => {
  const optionValues = new Set(dashboardOptions.map((item) => item.value));

  if (dashboardValue && optionValues.has(dashboardValue)) {
    return dashboardValue;
  }

  const firstRegion = value?.regions?.find((region) => optionValues.has(region));
  if (firstRegion) {
    return firstRegion;
  }

  const firstMarket = value?.markets?.[0] || '';
  if (!firstMarket) {
    return dashboardOptions?.[0]?.value || '';
  }

  if (optionValues.has(firstMarket)) {
    return firstMarket;
  }

  // option.value 拼接 id 后已不是 children.value 的简单前缀，按 children.value 全等匹配反推 parent
  const parentOption = dashboardOptions.find((opt) =>
    (opt.children || []).some((child) => child.value === firstMarket),
  );
  if (parentOption) {
    return parentOption.value;
  }

  return dashboardOptions?.[0]?.value || '';
};

/** 图表浮窗右列标题：固定为 Chart/图表。 */
export const resolveChartSecondLevelTitle = (
  _highlightedRegion: OptionsItemType | undefined,
  fixedTitle: string,
  _locale: string,
): string => fixedTitle;

